// Live parent-facing preview for the Class Editor.
//
// Renders the same ClassCard families see on the public site — driven by the
// current unsaved draft, so every edit updates before Save. A desktop/mobile
// width toggle mirrors the responsive breakpoints of /classes.
import { useState } from "react";
import { Monitor, Smartphone, Eye } from "lucide-react";
import ClassCard from "@/components/ClassCard";
import type { ClassItem } from "@/data/classes";

interface Props {
  classItem: ClassItem;
  additionalOptionsCount?: number;
}

export default function ClassPreviewPane({ classItem, additionalOptionsCount = 0 }: Props) {
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");

  return (
    <div className="flex flex-col h-full bg-muted/20">
      <div className="px-4 py-3 border-b bg-background/60 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <Eye className="h-3.5 w-3.5 text-primary" />
            Preview as parents will see it
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            Live · updates as you edit
          </div>
        </div>
        <div className="inline-flex rounded-md border bg-background shrink-0">
          <button
            type="button"
            onClick={() => setDevice("desktop")}
            className={`px-2 py-1 text-xs inline-flex items-center gap-1 rounded-l-md ${
              device === "desktop" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
            }`}
            aria-pressed={device === "desktop"}
          >
            <Monitor className="h-3.5 w-3.5" /> Desktop
          </button>
          <button
            type="button"
            onClick={() => setDevice("mobile")}
            className={`px-2 py-1 text-xs inline-flex items-center gap-1 rounded-r-md border-l ${
              device === "mobile" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
            }`}
            aria-pressed={device === "mobile"}
          >
            <Smartphone className="h-3.5 w-3.5" /> Mobile
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div
          className={`mx-auto transition-all duration-200 ${
            device === "mobile" ? "max-w-[340px]" : "max-w-[380px]"
          }`}
        >
          <ClassCard
            classItem={classItem}
            onClick={() => {
              /* preview is non-interactive */
            }}
            additionalOptionsCount={additionalOptionsCount}
          />
        </div>

        <div className="mt-4 mx-auto max-w-[420px] rounded-md border bg-background/70 p-3 text-[11px] text-muted-foreground leading-relaxed">
          This preview reflects your <strong>current unsaved edits</strong>. Click Save to publish the changes
          to the live class page.
        </div>
      </div>
    </div>
  );
}
